/**
 * Ausência por inatividade (spec 072), o lado da aba:
 *  - o aviso de atividade: só gesto da pessoa (tecla, clique, toque,
 *    rolagem), no máximo um por minuto, e só com status aberto. Quem derruba o
 *    status é o servidor (navegador fechado não conta tempo nenhum);
 *  - o aviso de volta: quando o status caiu por inatividade, em qualquer aba
 *    aberta. "Ficar disponível" abre o status de novo; "Continuar ausente"
 *    dispensa ESTA queda neste navegador — o `storage` leva a dispensa às
 *    outras abas, e o presence.changed leva a volta.
 * Os dois moram no chip de status, que existe nos dois layouts.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Presence } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/misc';
import { sendPresenceActivity, useSetMyPresence } from './presence-mutations';
import { presenceErrorMessage, presenceSettingsQuery } from './presence-queries';
import {
  ACTIVITY_EVENTS,
  IDLE_DISMISSED_KEY,
  idleDialogSince,
  idleMinutesLabel,
  idleSinceLabel,
  shouldSendActivity,
  tracksActivity,
} from './presence-idle';

export function useActivityHeartbeat(me: Presence | undefined): void {
  const enabled = tracksActivity(me);
  const lastSent = useRef<number | null>(null);
  useEffect(() => {
    if (!enabled) return;
    // status (re)aberto: o próximo gesto avisa na hora
    lastSent.current = null;
    const onGesture = () => {
      const now = Date.now();
      if (!shouldSendActivity(lastSent.current, now)) return;
      lastSent.current = now;
      void sendPresenceActivity();
    };
    for (const type of ACTIVITY_EVENTS)
      window.addEventListener(type, onGesture, { passive: true, capture: true });
    return () => {
      for (const type of ACTIVITY_EVENTS)
        window.removeEventListener(type, onGesture, { capture: true });
    };
  }, [enabled]);
}

function readDismissed(): string | null {
  try {
    return window.localStorage.getItem(IDLE_DISMISSED_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(value: string): void {
  try {
    window.localStorage.setItem(IDLE_DISMISSED_KEY, value);
  } catch {
    /* sem storage (janela privada): a dispensa vale só nesta aba */
  }
}

export function IdleReturnDialog({ me }: { me: Presence | undefined }) {
  const [dismissed, setDismissed] = useState<string | null>(readDismissed);
  const setStatus = useSetMyPresence();
  const settingsQ = useQuery(presenceSettingsQuery());
  const [error, setError] = useState<string | null>(null);

  // a dispensa numa aba fecha o aviso nas outras
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === IDLE_DISMISSED_KEY) setDismissed(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const since = idleDialogSince(me, dismissed);
  if (since === null) return null;

  const dismiss = () => {
    writeDismissed(since);
    setDismissed(since);
    setError(null);
  };
  const comeBack = () => {
    setError(null);
    setStatus.mutate(
      { status: 'available' },
      { onError: (e) => setError(presenceErrorMessage(e)) }
    );
  };
  const minutes = settingsQ.data?.idleMinutes;

  return (
    <Dialog open onClose={dismiss} title="Você saiu do Disponível">
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          Sem atividade desde{' '}
          <span className="font-medium text-foreground">{idleSinceLabel(since, Date.now())}</span>,
          o seu status passou para <span className="font-medium text-foreground">Ausente</span>
          {minutes ? ` (isso acontece depois de ${idleMinutesLabel(minutes)} sem atividade)` : ''}.
          A sua sessão continua aberta.
        </p>
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={dismiss}>
            Continuar ausente
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={setStatus.isPending}
            onClick={comeBack}
            autoFocus
          >
            Ficar disponível
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
